/**
 * Ditto-compatible theme support.
 *
 * Reads kind 16767 (active profile theme, replaceable) and kind 36767
 * (shareable theme definition, addressable) events and derives a full set of
 * shadcn/ui CSS variables from the three theme colors (background, text,
 * primary). VidStr applies the colors and fonts only — not Ditto's rounded
 * UI element styling.
 */

import type { NostrEvent } from '@nostrify/nostrify';
import type { CSSProperties } from 'react';

export interface DittoThemeColors {
  background: string;
  text: string;
  primary: string;
}

export interface DittoThemeFont {
  family: string;
  url: string;
  role: 'body' | 'title';
}

export interface DittoThemeBg {
  url: string;
  mode: 'cover' | 'tile';
  mimeType?: string;
}

export interface DittoTheme {
  colors: DittoThemeColors;
  fonts: DittoThemeFont[];
  bg?: DittoThemeBg;
  title?: string;
  /** a-tag reference to a kind 36767 theme definition (for attribution) */
  sourceRef?: string;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function sanitizeHex(value: string): string | null {
  return HEX_RE.test(value) ? value : null;
}

function sanitizeFontFamily(value: string): string | null {
  // Only allow safe font family characters — block CSS breakouts
  if (/^[a-zA-Z0-9 _-]{1,64}$/.test(value)) return value;
  return null;
}

function sanitizeUrl(value: string): string | null {
  try {
    const u = new URL(value);
    if (u.protocol !== 'https:') return null;
    // Return the normalized href: the URL parser percent-encodes characters
    // like `"` that could otherwise break out of a CSS url("…") context.
    return u.href;
  } catch {
    return null;
  }
}

/** Sanitize an event-sourced URL for use in CSS image contexts (https only). */
export function sanitizeHttpsUrl(value: string | undefined): string | null {
  if (!value) return null;
  return sanitizeUrl(value);
}

/** Parse a kind 16767 or 36767 event into a structured DittoTheme. */
export function parseThemeEvent(event: NostrEvent): DittoTheme | null {
  const colors: Partial<DittoThemeColors> = {};
  const fonts: DittoThemeFont[] = [];
  let bg: DittoThemeBg | undefined;
  let title: string | undefined;
  let sourceRef: string | undefined;

  for (const tag of event.tags) {
    if (tag[0] === 'c' && tag[1] && tag[2]) {
      const hex = sanitizeHex(tag[1]);
      const role = tag[2] as keyof DittoThemeColors;
      if (hex && (role === 'background' || role === 'text' || role === 'primary')) {
        colors[role] = hex;
      }
    }

    if (tag[0] === 'f' && tag[1] && tag[2]) {
      const family = sanitizeFontFamily(tag[1]);
      const url = sanitizeUrl(tag[2]);
      const role = (tag[3] as 'body' | 'title' | undefined) ?? 'body';
      if (family && url && (role === 'body' || role === 'title')) {
        fonts.push({ family, url, role });
      }
    }

    if (tag[0] === 'bg') {
      // variadic: ["bg", "url <url>", "mode <mode>", "m <mime>", ...]
      let bgUrl = '';
      let bgMode: 'cover' | 'tile' = 'cover';
      let mimeType: string | undefined;
      for (let i = 1; i < tag.length; i++) {
        const part = tag[i];
        if (part.startsWith('url ')) bgUrl = part.slice(4);
        else if (part.startsWith('mode ')) bgMode = part.slice(5) === 'tile' ? 'tile' : 'cover';
        else if (part.startsWith('m ')) mimeType = part.slice(2);
      }
      const safeUrl = bgUrl ? sanitizeUrl(bgUrl) : null;
      if (safeUrl) bg = { url: safeUrl, mode: bgMode, mimeType };
    }

    if (tag[0] === 'title' && tag[1]) title = tag[1].slice(0, 100);
    if (tag[0] === 'a' && tag[1]) sourceRef = tag[1];
  }

  if (!colors.background || !colors.text || !colors.primary) return null;

  return {
    colors: colors as DittoThemeColors,
    fonts,
    bg,
    title,
    sourceRef,
  };
}

// ── Color math ─────────────────────────────────────────────────────────────

function hexToHslValues(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta + 6) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function isDark(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance < 0.179;
}

function mixHex(a: string, b: string, t: number): string {
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  const rr = Math.round(ar + (br - ar) * t).toString(16).padStart(2, '0');
  const rg = Math.round(ag + (bg - ag) * t).toString(16).padStart(2, '0');
  const rb = Math.round(ab + (bb - ab) * t).toString(16).padStart(2, '0');
  return `#${rr}${rg}${rb}`;
}

/**
 * Derive the full set of shadcn CSS variable values from the 3 theme colors.
 * Returns { varName: "H S% L%" } pairs (no `--` prefix, no `hsl()`).
 */
export function deriveThemeCssVars(colors: DittoThemeColors): Record<string, string> {
  const { background, text, primary } = colors;
  const bgDark = isDark(background);

  const card = mixHex(background, text, bgDark ? 0.06 : 0.03);
  const muted = mixHex(background, text, bgDark ? 0.12 : 0.06);
  const border = mixHex(background, text, bgDark ? 0.18 : 0.12);
  const primaryFg = isDark(primary) ? '#ffffff' : '#000000';

  return {
    'background': hexToHslValues(background),
    'foreground': hexToHslValues(text),
    'card': hexToHslValues(card),
    'card-foreground': hexToHslValues(text),
    'popover': hexToHslValues(card),
    'popover-foreground': hexToHslValues(text),
    'primary': hexToHslValues(primary),
    'primary-foreground': hexToHslValues(primaryFg),
    'secondary': hexToHslValues(muted),
    'secondary-foreground': hexToHslValues(text),
    'muted': hexToHslValues(muted),
    'muted-foreground': hexToHslValues(mixHex(text, background, 0.45)),
    'accent': hexToHslValues(muted),
    'accent-foreground': hexToHslValues(text),
    'border': hexToHslValues(border),
    'input': hexToHslValues(border),
    'ring': hexToHslValues(primary),
  };
}

/** Build an inline style object with scoped CSS custom properties. */
export function buildThemeStyle(theme: DittoTheme): CSSProperties {
  const vars = deriveThemeCssVars(theme.colors);
  const cssVars: Record<string, string> = {};
  for (const [name, value] of Object.entries(vars)) {
    cssVars[`--${name}`] = `hsl(${value})`;
  }
  return cssVars as CSSProperties;
}

const injectedFonts = new Set<string>();

/** Inject @font-face rules into <head> and return the resolved font families. */
export function loadThemeFonts(theme: DittoTheme): { bodyFamily?: string; titleFamily?: string } {
  const result: { bodyFamily?: string; titleFamily?: string } = {};

  for (const font of theme.fonts) {
    const styleId = `ditto-font-${font.family.replace(/\s+/g, '-').toLowerCase()}`;
    if (!injectedFonts.has(styleId) && !document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent =
        `@font-face { font-family: '${font.family}'; src: url('${font.url}'); font-display: swap; }`;
      document.head.appendChild(style);
      injectedFonts.add(styleId);
    }
    if (font.role === 'body') result.bodyFamily = font.family;
    if (font.role === 'title') result.titleFamily = font.family;
  }

  return result;
}

/** Convert a hex color to an rgba() string with the given opacity. */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build a background style for a themed surface: the theme's background
 * color overlaid (semi-transparent) on the theme's background image, so the
 * image shows through without hurting readability. Falls back to the plain
 * background color when the theme has no bg image.
 */
export function buildThemeBackground(theme: DittoTheme, overlayOpacity = 0.85): CSSProperties {
  const style: CSSProperties = { backgroundColor: theme.colors.background };
  if (theme.bg) {
    const overlay = hexToRgba(theme.colors.background, overlayOpacity);
    style.backgroundImage = `linear-gradient(${overlay}, ${overlay}), url("${theme.bg.url}")`;
    style.backgroundSize = theme.bg.mode === 'tile' ? 'auto' : 'cover';
    style.backgroundRepeat = theme.bg.mode === 'tile' ? 'repeat' : 'no-repeat';
    style.backgroundPosition = 'center';
  }
  return style;
}
